/**
 * useRealtimeMessages - Supabase Realtime subscription for instant message updates
 *
 * When enabled, subscribes to postgres_changes on the messages and conversations tables.
 * Provides callbacks for new messages, conversation updates, etc.
 *
 * Feature-flagged: only active when Supabase auth and data are enabled.
 *
 * Shared-channel model
 * --------------------
 * Two places use this hook (Layout + useNotificationPolling) — earlier
 * revisions created a separate Supabase channel per hook mount (with a
 * random-suffix name to sidestep React's double-mount collision). That
 * meant 2-4 concurrent postgres_changes subscriptions per user which
 * tripped Realtime backpressure / connection limits → CHANNEL_ERROR.
 *
 * Now every mount for the same base channel name (`messages:global` or
 * `messages:conversation:<id>`) shares ONE Supabase channel. Handlers
 * fan out to a per-topic Set of live listener refs; when the last
 * listener detaches, the channel is removed. Safe under React 19's
 * double-mount because the second mount is a no-op reuse.
 */
import { useEffect, useRef, useCallback } from 'react';
import supabase, { isSupabaseAuthEnabled } from '../services/supabase';

const isRealtimeEnabled = () => {
  return process.env.REACT_APP_USE_SUPABASE_DATA === 'true' && isSupabaseAuthEnabled() && supabase;
};

// Module-level channel registry.
// key: base channel name → { channel, listeners: Set<callbacksRef> }
const channels = new Map();

function getOrCreateChannel(baseName, conversationId) {
  const existing = channels.get(baseName);
  if (existing) return existing;

  const channel = supabase.channel(baseName);
  const listeners = new Set();

  const messageFilter = conversationId
    ? { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` }
    : { event: 'INSERT', schema: 'public', table: 'messages' };
  channel.on('postgres_changes', messageFilter, (payload) => {
    listeners.forEach(ref => {
      try { ref.current.onNewMessage?.(payload.new); } catch (e) { console.error('[Realtime] listener error:', e); }
    });
  });

  const updateFilter = conversationId
    ? { event: 'UPDATE', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` }
    : { event: 'UPDATE', schema: 'public', table: 'messages' };
  channel.on('postgres_changes', updateFilter, (payload) => {
    listeners.forEach(ref => {
      try { ref.current.onMessageUpdate?.(payload.new, payload.old); } catch (e) { console.error('[Realtime] listener error:', e); }
    });
  });

  if (!conversationId) {
    // Conversation INSERT + UPDATE fan out to onConversationUpdate.
    // INSERT is needed too — a brand-new inbound conversation won't
    // be in the client's cached list yet, so a message-INSERT alone
    // races with the fetch and can leave the thread hidden until poll.
    channel.on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'conversations' },
      (payload) => listeners.forEach(ref => {
        try { ref.current.onConversationUpdate?.(payload.new, payload.old); } catch (e) { console.error('[Realtime] listener error:', e); }
      })
    );
    channel.on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'conversations' },
      (payload) => listeners.forEach(ref => {
        try { ref.current.onConversationUpdate?.(payload.new, null); } catch (e) { console.error('[Realtime] listener error:', e); }
      })
    );
  }

  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      console.log(`[Realtime] Subscribed to ${baseName}`);
    } else if (status === 'CHANNEL_ERROR') {
      console.warn(`[Realtime] Error subscribing to ${baseName}, falling back to polling`);
    }
  });

  const entry = { channel, listeners };
  channels.set(baseName, entry);
  return entry;
}

/**
 * Subscribe to real-time message events.
 */
export const useRealtimeMessages = ({
  onNewMessage,
  onMessageUpdate,
  onConversationUpdate,
  conversationId = null
} = {}) => {
  const callbacksRef = useRef({ onNewMessage, onMessageUpdate, onConversationUpdate });

  // Keep callbacks ref current so long-lived shared channel handlers
  // always see the latest closure without re-subscribing.
  useEffect(() => {
    callbacksRef.current = { onNewMessage, onMessageUpdate, onConversationUpdate };
  }, [onNewMessage, onMessageUpdate, onConversationUpdate]);

  useEffect(() => {
    if (!isRealtimeEnabled()) return;

    const baseName = conversationId
      ? `messages:conversation:${conversationId}`
      : 'messages:global';

    const entry = getOrCreateChannel(baseName, conversationId);
    entry.listeners.add(callbacksRef);

    return () => {
      entry.listeners.delete(callbacksRef);
      // Tear the channel down only when nobody's listening. Leaving an
      // idle channel is fine (Supabase's client bounds them) but freeing
      // eagerly avoids stale postgres_changes traffic when the last
      // subscriber navigates away.
      if (entry.listeners.size === 0) {
        supabase.removeChannel(entry.channel);
        channels.delete(baseName);
      }
    };
  }, [conversationId]);

  // Manual unsubscribe used by callers that want fine-grained control.
  const unsubscribe = useCallback(() => {
    for (const [name, entry] of channels.entries()) {
      if (entry.listeners.has(callbacksRef)) {
        entry.listeners.delete(callbacksRef);
        if (entry.listeners.size === 0) {
          supabase.removeChannel(entry.channel);
          channels.delete(name);
        }
      }
    }
  }, []);

  return { isActive: isRealtimeEnabled(), unsubscribe };
};

export default useRealtimeMessages;
